<template>
  <main class="analytics-page">
    <header class="analytics-header">
      <div>
        <p class="analytics-kicker">Lab intelligence</p>
        <h1>Analytics Dashboard</h1>
      </div>
      <button type="button" class="refresh-command" :disabled="loading" @click="refresh">
        <span class="material-symbols-outlined" aria-hidden="true">refresh</span>
        Refresh
      </button>
    </header>

    <section class="kpi-grid" aria-label="Inventory summary">
      <article v-for="item in kpis" :key="item.label" class="kpi-card">
        <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
        <div>
          <p>{{ item.label }}</p>
          <strong>{{ item.value }}</strong>
        </div>
      </article>
    </section>

    <section class="analytics-grid">
      <article class="analytics-panel wide">
        <header>
          <h2>Activity Trends</h2>
          <span>Last 7 days</span>
        </header>
        <div class="bar-chart" aria-label="Activity over time">
          <div v-for="day in activitySeries" :key="day.key" class="bar-column">
            <div class="bar-track">
              <span :style="{ height: `${day.height}%` }"></span>
            </div>
            <strong>{{ day.count }}</strong>
            <em>{{ day.label }}</em>
          </div>
        </div>
      </article>

      <article class="analytics-panel">
        <header>
          <h2>Items By Project</h2>
          <span>{{ projectRows.length }} projects</span>
        </header>
        <div class="rank-list">
          <div v-for="row in projectRows" :key="row.id" class="rank-row">
            <div>
              <strong>{{ row.name }}</strong>
              <span>{{ row.count }} linked items</span>
            </div>
            <meter min="0" :max="maxProjectCount" :value="row.count"></meter>
          </div>
          <div v-if="projectRows.length === 0" class="empty-note">
            No project assignments yet.
          </div>
        </div>
      </article>

      <article class="analytics-panel">
        <header>
          <h2>Top Active Users</h2>
          <span>{{ userRows.length }} users</span>
        </header>
        <div class="rank-list">
          <div v-for="row in userRows" :key="row.id" class="rank-row compact">
            <span class="avatar" aria-hidden="true">{{ initialsFor(row.name) }}</span>
            <div>
              <strong>{{ row.name }}</strong>
              <span>{{ row.count }} actions</span>
            </div>
          </div>
          <div v-if="userRows.length === 0" class="empty-note">
            No activity logged yet.
          </div>
        </div>
      </article>

      <article class="analytics-panel wide">
        <header>
          <h2>Occupancy Heatmap</h2>
          <span>{{ occupancyRows.length }} pucks</span>
        </header>
        <div class="occupancy-list">
          <div v-for="row in occupancyRows" :key="row.id" class="occupancy-row">
            <div class="occupancy-label">
              <strong>{{ row.title }}</strong>
              <span>{{ row.path }}</span>
            </div>
            <div class="occupancy-wells" :style="{ '--well-count': row.wellCount }">
              <span
                v-for="well in row.wells"
                :key="well.index"
                :class="{ occupied: well.occupied }"
                :title="`Well ${well.index}: ${well.occupied ? 'Occupied' : 'Available'}`"
              ></span>
            </div>
            <em>{{ row.occupied }}/{{ row.wellCount }}</em>
          </div>
          <div v-if="occupancyRows.length === 0" class="empty-note">
            No pucks available for occupancy tracking.
          </div>
        </div>
      </article>

      <article class="analytics-panel wide">
        <header>
          <h2>Activity Timeline</h2>
          <span>{{ timelineRows.length }} recent events</span>
        </header>
        <div class="timeline-list">
          <div v-for="log in timelineRows" :key="log.id" class="timeline-row">
            <span class="timeline-dot" aria-hidden="true"></span>
            <div>
              <strong>{{ log.actor }}</strong>
              <p>{{ log.text }}</p>
            </div>
            <time>{{ log.date }}</time>
          </div>
          <div v-if="timelineRows.length === 0" class="empty-note">
            No recent events.
          </div>
        </div>
      </article>
    </section>
  </main>
</template>

<script>
function canonicalId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.id || "";
}

function labelFor(entity, fallback) {
  return entity?.title || entity?.name || entity?.customId || entity?.serialNo || fallback;
}

function collectDewars(source) {
  const result = [];
  const visit = (value) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value.racks)) {
      result.push(value);
      return;
    }
    if (Array.isArray(value.data)) visit(value.data);
  };
  visit(source);
  return result;
}

function boxesForPuck(puck) {
  const map = new Map();
  if (Array.isArray(puck?.boxes)) {
    puck.boxes.forEach((box) => {
      const id = canonicalId(box);
      if (id) map.set(id, box);
    });
  }
  for (let index = 1; index <= 12; index += 1) {
    const well = puck?.[`well${index}`];
    if (well && typeof well === "object") {
      const id = canonicalId(well.box || well);
      if (id) map.set(id, well.box || well);
    }
  }
  return [...map.values()];
}

export default {
  name: "AnalyticsDashboard",
  data() {
    return {
      loading: false,
      lastLoadedAt: null,
    };
  },
  computed: {
    activeLabId() {
      return canonicalId(this.appStore.state.activeUser?.activeLabId) || canonicalId(this.appStore.state.activeUser?.labId);
    },
    dewars() {
      return collectDewars(this.appStore.state.dewarData);
    },
    inventoryCounts() {
      const seen = {
        racks: new Set(),
        pucks: new Set(),
        boxes: new Set(),
        grids: new Set(),
      };
      this.dewars.forEach((dewar) => {
        (dewar.racks || []).forEach((rack) => {
          if (canonicalId(rack)) seen.racks.add(canonicalId(rack));
          (rack.pucks || []).forEach((puck) => {
            if (canonicalId(puck)) seen.pucks.add(canonicalId(puck));
            boxesForPuck(puck).forEach((box) => {
              if (canonicalId(box)) seen.boxes.add(canonicalId(box));
              (box.grids || []).forEach((grid) => {
                if (canonicalId(grid)) seen.grids.add(canonicalId(grid));
              });
            });
          });
        });
      });
      return {
        dewars: this.dewars.length,
        racks: seen.racks.size,
        pucks: seen.pucks.size,
        boxes: seen.boxes.size,
        grids: seen.grids.size,
      };
    },
    kpis() {
      const totalWells = this.occupancyRows.reduce((sum, row) => sum + row.wellCount, 0);
      const occupied = this.occupancyRows.reduce((sum, row) => sum + row.occupied, 0);
      const occupancy = totalWells ? Math.round((occupied / totalWells) * 100) : 0;
      return [
        { label: "Dewars", value: this.inventoryCounts.dewars, icon: "device_thermostat" },
        { label: "Racks", value: this.inventoryCounts.racks, icon: "view_module" },
        { label: "Pucks", value: this.inventoryCounts.pucks, icon: "radio_button_checked" },
        { label: "Boxes", value: this.inventoryCounts.boxes, icon: "inventory_2" },
        { label: "Grids", value: this.inventoryCounts.grids, icon: "grid_on" },
        { label: "Occupancy", value: `${occupancy}%`, icon: "donut_large" },
      ];
    },
    logs() {
      return this.appStore.state.logs || [];
    },
    activitySeries() {
      const days = [];
      const now = new Date();
      for (let index = 6; index >= 0; index -= 1) {
        const day = new Date(now);
        day.setDate(now.getDate() - index);
        const key = day.toISOString().slice(0, 10);
        days.push({
          key,
          label: day.toLocaleDateString("en-US", { weekday: "short" }),
          count: 0,
        });
      }
      this.logs.forEach((log) => {
        const key = new Date(log.createdAt).toISOString().slice(0, 10);
        const target = days.find((day) => day.key === key);
        if (target) target.count += 1;
      });
      const max = Math.max(1, ...days.map((day) => day.count));
      return days.map((day) => ({
        ...day,
        height: Math.max(6, Math.round((day.count / max) * 100)),
      }));
    },
    projectRows() {
      const projects = this.appStore.state.projects || [];
      const counts = new Map(projects.map((project) => [canonicalId(project), 0]));
      this.dewars.forEach((dewar) => {
        (dewar.racks || []).forEach((rack) => {
          const rackProjectId = canonicalId(rack.project || rack.projectId);
          if (rackProjectId) counts.set(rackProjectId, (counts.get(rackProjectId) || 0) + 1);
          (rack.pucks || []).forEach((puck) => {
            const puckProjectId = canonicalId(puck.project || puck.projectId);
            if (puckProjectId) counts.set(puckProjectId, (counts.get(puckProjectId) || 0) + 1);
            boxesForPuck(puck).forEach((box) => {
              const boxProjectId = canonicalId(box.project || box.projectId);
              if (boxProjectId) counts.set(boxProjectId, (counts.get(boxProjectId) || 0) + 1);
            });
          });
        });
      });
      return projects
        .map((project) => ({
          id: canonicalId(project),
          name: project.name || "Untitled project",
          count: counts.get(canonicalId(project)) || 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    },
    maxProjectCount() {
      return Math.max(1, ...this.projectRows.map((row) => row.count));
    },
    userRows() {
      const counts = new Map();
      this.logs.forEach((log) => {
        const id = canonicalId(log.user);
        if (!id) return;
        const name = log.user?.name || log.user?.email || "User";
        const current = counts.get(id) || { id, name, count: 0 };
        current.count += 1;
        counts.set(id, current);
      });
      return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 6);
    },
    occupancyRows() {
      const rows = [];
      this.dewars.forEach((dewar) => {
        (dewar.racks || []).forEach((rack) => {
          (rack.pucks || []).forEach((puck) => {
            const wellCount = Number(puck.wellCount || 12);
            const boxes = boxesForPuck(puck);
            const occupiedWells = new Set(
              boxes.map((box) => Number(box.wellNo || box.well || box.position)).filter(Boolean)
            );
            rows.push({
              id: canonicalId(puck) || `${canonicalId(rack)}-${rows.length}`,
              title: labelFor(puck, "Puck"),
              path: [labelFor(dewar, "Dewar"), labelFor(rack, "Rack")].filter(Boolean).join(" / "),
              wellCount,
              occupied: occupiedWells.size,
              wells: Array.from({ length: wellCount }, (_, index) => ({
                index: index + 1,
                occupied: occupiedWells.has(index + 1),
              })),
            });
          });
        });
      });
      return rows.sort((a, b) => b.occupied - a.occupied).slice(0, 10);
    },
    timelineRows() {
      return this.logs.slice(0, 12).map((log) => {
        const connection = Array.isArray(log.connections) ? log.connections[0] : null;
        const itemType = connection?.itemType || "item";
        return {
          id: canonicalId(log),
          actor: log.user?.email || log.user?.name || "User",
          text: log.isLogin
            ? "logged in"
            : `${log.verb || "updated"} ${itemType === "Apikey" ? "an API key" : itemType}`,
          date: this.formatDate(log.createdAt),
        };
      });
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      const labid = this.activeLabId;
      try {
        await Promise.allSettled([
          this.appStore.dispatch("updateDewarData"),
          this.appStore.dispatch("getAdminStuff", { url: "projects" }),
          labid ? this.appStore.dispatch("getAdminStuff", { url: "logs", labid }) : Promise.resolve(),
        ]);
        this.lastLoadedAt = new Date();
      } finally {
        this.loading = false;
      }
    },
    initialsFor(name) {
      return (
        String(name || "?")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join("") || "?"
      );
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.analytics-page {
  display: grid;
  align-content: start;
  gap: 16px;
  min-height: calc(100vh - 24px);
  padding: 16px;
  background: #fff;
  color: #172033;
}

.analytics-header,
.analytics-panel,
.kpi-card {
  border: 1px solid #d7e2ef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 22px rgba(15, 35, 55, 0.07);
}

.analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px;
}

.analytics-kicker {
  margin: 0 0 4px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.analytics-header h1 {
  margin: 0;
  color: #0f3654;
  font-size: 1.55rem;
  font-weight: 900;
}

.refresh-command {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: auto;
  min-height: 36px;
  margin: 0;
  border: 1px solid #0b4f71;
  border-radius: 7px;
  background: #0b4f71;
  padding: 0 13px;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
}

.refresh-command:disabled {
  opacity: 0.65;
  cursor: wait;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
}

.kpi-card {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 12px;
}

.kpi-card .material-symbols-outlined {
  color: #0b4f71;
  font-size: 28px;
}

.kpi-card p {
  margin: 0 0 2px;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
}

.kpi-card strong {
  color: #123a59;
  font-size: 1.35rem;
  line-height: 1;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.analytics-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 16px;
}

.analytics-panel.wide {
  grid-column: 1 / -1;
}

.analytics-panel header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #dbe4ee;
  padding-bottom: 10px;
}

.analytics-panel h2 {
  margin: 0;
  color: #0f3654;
  font-size: 1rem;
  font-weight: 900;
}

.analytics-panel header span {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
}

.bar-chart {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  min-height: 220px;
  align-items: end;
}

.bar-column {
  display: grid;
  grid-template-rows: 1fr auto auto;
  gap: 6px;
  min-width: 0;
  text-align: center;
}

.bar-track {
  display: flex;
  align-items: end;
  height: 160px;
  border: 1px solid #d7e2ef;
  border-radius: 6px;
  background: #f8fbff;
  overflow: hidden;
}

.bar-track span {
  display: block;
  width: 100%;
  background: linear-gradient(180deg, #0b4f71, #0a7a3d);
}

.bar-column strong {
  color: #123a59;
  font-size: 0.8rem;
}

.bar-column em {
  color: #64748b;
  font-size: 0.68rem;
  font-style: normal;
  font-weight: 900;
}

.rank-list,
.occupancy-list,
.timeline-list {
  display: grid;
  gap: 9px;
}

.rank-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  align-items: center;
  gap: 10px;
  border: 1px solid #d7e2ef;
  border-radius: 7px;
  background: #f8fbff;
  padding: 10px;
}

.rank-row.compact {
  grid-template-columns: 36px minmax(0, 1fr);
}

.rank-row strong,
.occupancy-label strong,
.timeline-row strong {
  display: block;
  overflow: hidden;
  color: #172033;
  font-size: 0.85rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-row span,
.occupancy-label span,
.timeline-row p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
}

.rank-row meter {
  width: 100%;
  height: 10px;
}

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0b4f71;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 900;
}

.occupancy-row {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(0, 1fr) 52px;
  align-items: center;
  gap: 12px;
  border: 1px solid #d7e2ef;
  border-radius: 7px;
  background: #f8fbff;
  padding: 10px;
}

.occupancy-wells {
  display: grid;
  grid-template-columns: repeat(var(--well-count), minmax(9px, 1fr));
  gap: 4px;
}

.occupancy-wells span {
  display: block;
  aspect-ratio: 1;
  border: 1px solid #cbd7e6;
  border-radius: 999px;
  background: #fff;
}

.occupancy-wells span.occupied {
  border-color: #0a7a3d;
  background: #0a7a3d;
}

.occupancy-row em {
  color: #123a59;
  font-size: 0.78rem;
  font-style: normal;
  font-weight: 900;
  text-align: right;
}

.timeline-row {
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border: 1px solid #d7e2ef;
  border-radius: 7px;
  background: #f8fbff;
  padding: 10px;
}

.timeline-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #0a7a3d;
}

.timeline-row time {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}

.empty-note {
  display: grid;
  place-items: center;
  min-height: 90px;
  border: 1px dashed #cbd7e6;
  border-radius: 7px;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 800;
}

@media (max-width: 1120px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .analytics-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .analytics-header,
  .analytics-panel header,
  .occupancy-row,
  .timeline-row {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .analytics-header {
    flex-direction: column;
  }

  .refresh-command {
    justify-content: center;
    width: 100%;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bar-chart {
    gap: 6px;
  }

  .rank-row {
    grid-template-columns: 1fr;
  }

  .timeline-row time {
    white-space: normal;
  }
}
</style>
